import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, router } from '@inertiajs/react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const WorkspaceScene = ({ scenes, movieHash, workspaceCounts, workspaces }) => {
  const [selectedSceneNumber, setSelectedSceneNumber] = useState('');
  const [sceneText, setSceneText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditingText, setIsEditingText] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [themeAlignmentSuggestions, setThemeAlignmentSuggestions] = useState([]);
  const [philosophicalConflictSuggestions, setPhilosophicalConflictSuggestions] = useState([]);
  const [customDirections, setCustomDirections] = useState('');

  // For per-suggestion editing/tracking
  const [editableSuggestions, setEditableSuggestions] = useState({});
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);

  // A small helper that preserves scroll around a state change
  const preserveScroll = (action) => {
    // Store current scroll position
    const currentScroll = window.scrollY;
    // Run whatever action updates state
    action();
    // Restore scroll after the next microtask
    setTimeout(() => {
      window.scrollTo(0, currentScroll);
    }, 0);
  };

  useEffect(() => {
    console.log('isSubmitting changed:', isSubmitting);
  }, [isSubmitting]);

  useEffect(() => {
    const selectedScene = scenes.find(
      (scene) => scene.scene_number === parseInt(selectedSceneNumber)
    );

    if (selectedScene) {
      // Scene text
      setSceneText(selectedScene.text || '');

      // Regular suggestions
      if (selectedScene.long_suggestions) {
        try {
          const parsed = typeof selectedScene.long_suggestions === 'string'
            ? JSON.parse(selectedScene.long_suggestions)
            : selectedScene.long_suggestions;
          setSuggestions(Array.isArray(parsed) ? parsed : []);
        } catch (error) {
          console.error('Error parsing long_suggestions:', error);
          setSuggestions([]);
        }
      } else {
        setSuggestions([]);
      }

      // Theme + Philosophical suggestions
      if (selectedScene.alignments) {
        try {
          const alignments = typeof selectedScene.alignments === 'string'
            ? JSON.parse(selectedScene.alignments)
            : selectedScene.alignments;

          if (alignments.suggestions?.themeAlignment) {
            const ta = alignments.suggestions.themeAlignment;
            setThemeAlignmentSuggestions(Array.isArray(ta) ? ta : []);
          } else {
            setThemeAlignmentSuggestions([]);
          }

          if (alignments.suggestions?.philosophicalConflictAlignment) {
            const pc = alignments.suggestions.philosophicalConflictAlignment;
            setPhilosophicalConflictSuggestions(Array.isArray(pc) ? pc : []);
          } else {
            setPhilosophicalConflictSuggestions([]);
          }
        } catch (error) {
          console.error('Error parsing alignments:', error);
          setThemeAlignmentSuggestions([]);
          setPhilosophicalConflictSuggestions([]);
        }
      } else {
        setThemeAlignmentSuggestions([]);
        setPhilosophicalConflictSuggestions([]);
      }
    } else {
      // Reset if no scene selected
      setSceneText('');
      setSuggestions([]);
      setThemeAlignmentSuggestions([]);
      setPhilosophicalConflictSuggestions([]);
    }

    // Reset selections + custom directions
    setSelectedSuggestions([]);
    setCustomDirections('');
    setEditableSuggestions({});
  }, [selectedSceneNumber, scenes]);

  // When suggestions change, rebuild our editable suggestions dictionary
  useEffect(() => {
    const initEdits = {};
    [
      ...suggestions,
      ...themeAlignmentSuggestions,
      ...philosophicalConflictSuggestions,
    ].forEach((item, idx) => {
      initEdits[item] = item; // item text => item text
    });
    setEditableSuggestions(initEdits);
  }, [suggestions, themeAlignmentSuggestions, philosophicalConflictSuggestions]);

  const handleSceneChange = (e) => {
    // Use preserveScroll to prevent jumping
    preserveScroll(() => {
      setSelectedSceneNumber(e.target.value);
      setIsEditingText(false);
    });
  };

  const toggleEditText = () => {
    setIsEditingText(!isEditingText);
  };

  const handleTextChange = (e) => {
    setSceneText(e.target.value);
  };

  const handleCustomDirectionsChange = (e) => {
    setCustomDirections(e.target.value);
  };

  // Toggling a suggestion
  const handleSuggestionChange = (originalSuggestion) => {
    preserveScroll(() => {
      const isAlreadySelected = selectedSuggestions.includes(
        editableSuggestions[originalSuggestion]
      );
      if (isAlreadySelected) {
        setSelectedSuggestions((prev) =>
          prev.filter((text) => text !== editableSuggestions[originalSuggestion])
        );
      } else {
        setSelectedSuggestions((prev) => [
          ...prev,
          editableSuggestions[originalSuggestion],
        ]);
      }
    });
  };

  // Changing text of a suggestion
  const handleSuggestionTextChange = (originalSuggestion, newText) => {
    setEditableSuggestions((prev) => ({
      ...prev,
      [originalSuggestion]: newText,
    }));

    // If it's currently selected, update it in the list
    if (selectedSuggestions.includes(prev => prev[originalSuggestion])) {
      setSelectedSuggestions((prev) => {
        const filtered = prev.filter(
          (text) => text !== prev[originalSuggestion]
        );
        return [...filtered, newText];
      });
    }
  };

  // Select all or none for a given array of suggestions
  const handleSelectAll = (items, shouldSelect = true) => {
    preserveScroll(() => {
      if (shouldSelect) {
        // Add all that aren't already selected
        const toAdd = items
          .map((it) => editableSuggestions[it])
          .filter((text) => !selectedSuggestions.includes(text));
        setSelectedSuggestions((prev) => [...prev, ...toAdd]);
      } else {
        // Remove all from selected
        const toRemove = items.map((it) => editableSuggestions[it]);
        setSelectedSuggestions((prev) =>
          prev.filter((text) => !toRemove.includes(text))
        );
      }
    });
  };

  // Filter out the relevant workspaces for the chosen scene
  const filteredWorkspaces = workspaces.filter(
    (ws) => ws.scene_number === parseInt(selectedSceneNumber)
  );

  // Summaries
  const totalSelected = selectedSuggestions.length;

  // On form submission
  const handleSubmit = useCallback(
    async (route) => {
      if (!selectedSceneNumber) {
        toast.warn('Please select a scene.');
        return;
      }

      setIsSubmitting(true);
      console.log('Submitting at:', new Date().toISOString());

      try {
        await new Promise((resolve) => {
          router.post(
            `/screenplay/${movieHash}/${route}`,
            {
              scene_number: selectedSceneNumber,
              scene_text: isEditingText ? sceneText : null,
              todo: JSON.stringify({
                selectedSuggestions,
                customDirections,
              }),
            },
            {
              preserveState: true,
              preserveScroll: true, // Also preserve scroll on Inertia navigation
              onFinish: () => {
                console.log(
                  'Inertia request finished at:',
                  new Date().toISOString()
                );
                resolve();
              },
            }
          );
        });

        console.log('Request officially completed at:', new Date().toISOString());
        toast.success('Scene updated successfully.');
      } catch (error) {
        console.error('Error sending scene:', error);
        toast.error('Failed to send scene.');
      } finally {
        console.log('Finally block executed at:', new Date().toISOString());
        setIsSubmitting(false);
      }
    },
    [
      selectedSceneNumber,
      movieHash,
      isEditingText,
      sceneText,
      selectedSuggestions,
      customDirections,
    ]
  );

  // Sub-component for listing suggestions in one category
  const SuggestionsList = ({ title, items }) => {
    if (!items.length) {
      return (
        <div className="mb-3">
          <h4>{title}</h4>
          <p className="text-muted">No suggestions.</p>
        </div>
      );
    }

    const allSelected = items.every((suggestion) =>
      selectedSuggestions.includes(editableSuggestions[suggestion])
    );

    const noneSelected = items.every(
      (suggestion) => !selectedSuggestions.includes(editableSuggestions[suggestion])
    );

    return (
      <div className="mb-3">
        <h4>{title}</h4>
        <div className="d-flex gap-2 mb-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => handleSelectAll(items, true)}
            disabled={allSelected}
            title="Select all suggestions in this category"
          >
            Select All
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => handleSelectAll(items, false)}
            disabled={noneSelected}
            title="Deselect all suggestions in this category"
          >
            Deselect All
          </button>
        </div>
        {items.map((originalSuggestion, i) => {
          const isChecked = selectedSuggestions.includes(
            editableSuggestions[originalSuggestion]
          );
          return (
            <div className="d-flex mb-2 align-items-start" key={`${title}-${i}`}>
              <div className="form-check me-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`chk-${title}-${i}`}
                  checked={isChecked}
                  onChange={() => handleSuggestionChange(originalSuggestion)}
                />
              </div>
              <textarea
                className="form-control"
                rows={3}
                value={editableSuggestions[originalSuggestion] || ''}
                onChange={(e) =>
                  handleSuggestionTextChange(originalSuggestion, e.target.value)
                }
              />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="container py-4">
      <div className="alert alert-info mb-4">
        <strong>Instructions:</strong> Select a scene you want to work on.
      </div>
      
      {/* Scene Selection */}
      <div className="mb-3">
        <label htmlFor="sceneSelect" className="form-label">Select Scene</label>
        <select
          id="sceneSelect"
          className="form-select"
          value={selectedSceneNumber}
          onChange={handleSceneChange}
        >
          <option value="">-- Select a Scene --</option>
          {scenes.map((scene) => {
            const count = workspaceCounts[scene.scene_number] || 0;
            return (
              <option key={scene.scene_number} value={scene.scene_number}>
                {scene.scene_number} - {scene.scene_title} ({count} workspace
                {count !== 1 && 's'})
              </option>
            );
          })}
        </select>
      </div>

      {/* Existing Workspaces */}
      {selectedSceneNumber && (
        <div className="mb-4">
          <h5>You can continue an existing Workspace for Scene {selectedSceneNumber}:</h5>
          {filteredWorkspaces.length > 0 ? (
            <ul>
              {filteredWorkspaces.map((ws) => (
                <li key={ws.uuid}>
                  <Link 
                    href={`/workspace/${ws.uuid}`}
                    preserveScroll
                    title="Continue this existing workspace"
                  >
                    {ws.date} - {ws.level === 'scene_text' ? 'Scene Execution' : `Level: ${ws.level}`}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p>No workspaces found for this scene.</p>
          )}
        </div>
      )}

      {selectedSceneNumber && (
        <>
          {/* Scene Editing */}
          <div className="alert alert-info mb-4">
            <p><strong>Working with this scene:</strong></p>
            <p>
              You can edit the text or leave it as is while you explore suggestions. 
              Browse through them below and add your own custom directions to steer the 
              creative process. Check the suggestions you’d like to include.
            </p>
          </div>
          
          <div className="mb-3">
            <button
              className={`btn ${isEditingText ? 'btn-secondary' : 'btn-outline-primary'} mb-3`}
              onClick={toggleEditText}
              title="Toggle editing of the main scene text"
            >
              {isEditingText ? 'Cancel Edit Text' : 'Edit Text'}
            </button>
            
            {isEditingText && (
              <>
                <label htmlFor="sceneText" className="form-label">Scene Text</label>
                <textarea
                  id="sceneText"
                  className="form-control"
                  value={sceneText}
                  onChange={handleTextChange}
                  rows={10}
                  placeholder="Enter scene text here..."
                />
              </>
            )}
          </div>

          {/* Suggestions: Regular, Theme, Philosophical */}
          <SuggestionsList 
            title="Regular Suggestions" 
            items={suggestions} 
          />
          <SuggestionsList 
            title="Theme Alignment Suggestions" 
            items={themeAlignmentSuggestions} 
          />
          <SuggestionsList 
            title="Philosophical Conflict Suggestions" 
            items={philosophicalConflictSuggestions} 
          />

          {/* Custom Directions */}
          <div className="mb-4">
            <h4>Custom Directions</h4>
            <div className="alert alert-light mb-2">
              What direction do you want to take this scene? Let your creativity shine!
            </div>
            <textarea
              className="form-control"
              value={customDirections}
              onChange={handleCustomDirectionsChange}
              rows={4}
              placeholder="Add your own custom directions here..."
            />
          </div>

          {/* Summary & Action Buttons */}
          <div className="mb-3">
            <p>
              <strong>{totalSelected}</strong> suggestion(s) selected.
            </p>
          </div>
        </>
      )}

      {/* Main Action Buttons */}
      <div className="d-flex gap-2">
        <button
          className="btn btn-primary"
          onClick={() => handleSubmit('workspace_scene_design')}
          disabled={isSubmitting}
          title="Focus on pre-production ideas and structure"
        >
          {isSubmitting ? (
            <div>
              <span 
                className="spinner-border spinner-border-sm" 
                role="status" 
                aria-hidden="true"
              ></span> Scene Design
            </div>
          ) : (
            'Scene Design'
          )}
        </button>

        <button
          className="btn btn-success"
          onClick={() => handleSubmit('workspace_scene_execution')}
          disabled={isSubmitting || !selectedSceneNumber}
          title="Refine or finalize this scene"
        >
          {isSubmitting ? (
            <div>
              <span 
                className="spinner-border spinner-border-sm" 
                role="status" 
                aria-hidden="true"
              ></span> Scene Execution
            </div>
          ) : (
            'Scene Execution'
          )}
        </button>

        <button
          className="btn btn-success"
          onClick={() => handleSubmit('workspace_scene_dialogue')}
          disabled={isSubmitting || !selectedSceneNumber}
          title="Refine the dialogue"
        >
          {isSubmitting ? (
            <div>
              <span 
                className="spinner-border spinner-border-sm" 
                role="status" 
                aria-hidden="true"
              ></span> Scene Dialogue
            </div>
          ) : (
            'Scene Dialogue'
          )}
        </button>

      </div>

      <ToastContainer />
    </div>
  );
};

export default WorkspaceScene;
